import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

// Integrated logo animation — figure lives inside the logo, bleeds into the water
const LogoAnimation = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Smaller animation on mobile
    const isMobile = window.innerWidth < 768;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    const figureImg = new Image();
    let figureLoaded = false;
    figureImg.onload = () => { figureLoaded = true; };
    figureImg.src = '/falling-figure-gold.png';

    // The figure's home position — centered in the logo
    const homeX = W * 0.5;
    const homeY = H * 0.28;

    // States: waiting → appearing → resting → releasing → fading → waiting
    // TIMING: less frequent, more dramatic slow-motion drop
    let state = 'waiting';
    let stateTimer = 300; // 5s initial wait — let the logo breathe
    let figY = homeY;
    let figAlpha = 0;
    let figScale = 1;
    let sinkProgress = 0;
    let breathCycle = 0;

    // Blood drip pool
    const drips = [];
    const createDrip = (fromFigure) => ({
      x: fromFigure ? homeX + (Math.random() - 0.5) * 40 : W * 0.2 + Math.random() * W * 0.6,
      y: fromFigure ? figY + 60 : H * 0.55 + Math.random() * H * 0.08,
      speed: 0.2 + Math.random() * 0.3,
      opacity: 0.3 + Math.random() * 0.3,
      width: 1 + Math.random() * 1.5,
      progress: 0,
      maxDrop: 20 + Math.random() * 40,
      isGold: fromFigure && Math.random() > 0.5,
    });

    let frame = 0;
    let animId;

    const drawFigureAt = (x, y, alpha, scale) => {
      if (!figureLoaded || alpha <= 0) return;
      ctx.save();
      ctx.globalAlpha = alpha;
      const w = (isMobile ? 35 : 50) * scale;
      const h = w * (figureImg.naturalHeight / figureImg.naturalWidth);
      // Subtle warm gold glow — crisp, not blurry
      ctx.shadowColor = 'rgba(180, 150, 60, 0.3)';
      ctx.shadowBlur = 6 * alpha;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(figureImg, x - w / 2, y - h / 2, w, h);
      ctx.restore();
    };

    // Water ripple rings where figure enters water
    const drawWaterEntry = (x, y, progress) => {
      const alpha = Math.max(0, 1 - progress / 80);
      if (alpha <= 0) return;
      for (let i = 0; i < 3; i++) {
        const r = progress * (1.5 + i * 0.8);
        const ringAlpha = alpha * (0.4 - i * 0.12);
        if (ringAlpha <= 0) continue;
        ctx.strokeStyle = `rgba(180, 150, 60, ${ringAlpha})`;
        ctx.lineWidth = 1.5 - i * 0.4;
        ctx.beginPath();
        ctx.ellipse(x, y, r, r * 0.25, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      frame++;
      breathCycle += 0.02;

      // --- State machine ---
      stateTimer--;

      if (state === 'waiting' && stateTimer <= 0) {
        state = 'appearing';
        figY = homeY;
        figAlpha = 0;
        figScale = 1;
        stateTimer = 180; // 3s slow fade in
      }

      if (state === 'appearing') {
        figAlpha = Math.min(0.65, figAlpha + 0.004); // slower fade in
        const breathOffset = Math.sin(breathCycle) * 1;
        drawFigureAt(homeX, homeY + breathOffset, figAlpha, 1);
        if (stateTimer <= 0) {
          state = 'resting';
          stateTimer = 180 + Math.random() * 120; // 3-5s visible rest
        }
      }

      if (state === 'resting') {
        const breathOffset = Math.sin(breathCycle) * 1.5;
        const breathAlpha = 0.5 + Math.sin(breathCycle * 0.7) * 0.1;
        drawFigureAt(homeX, homeY + breathOffset, breathAlpha, 1);
        if (frame % 50 === 0) drips.push(createDrip(true));
        if (stateTimer <= 0) {
          state = 'releasing';
          figY = homeY;
          figAlpha = 0.65;
          sinkProgress = 0;
          stateTimer = 360; // 6s dramatic slow-motion fall
        }
      }

      if (state === 'releasing') {
        // SLOW-MOTION heavy fall — much slower than before
        const fallSpeed = 0.3 + sinkProgress * 0.008;
        figY += fallSpeed;
        sinkProgress++;
        const waterLine = H * 0.62;
        if (figY > waterLine) {
          figAlpha = Math.max(0, figAlpha - 0.006); // slower fade
          figScale = Math.max(0.7, figScale - 0.0015);
        }
        drawFigureAt(homeX, figY, figAlpha, figScale);
        if (figY > waterLine && sinkProgress > 0) {
          drawWaterEntry(homeX, waterLine, sinkProgress - (waterLine - homeY) / fallSpeed);
        }
        if (frame % 12 === 0 && figAlpha > 0.1) drips.push(createDrip(true));
        if (stateTimer <= 0 || figAlpha <= 0) {
          state = 'fading';
          stateTimer = 150; // 2.5s ripple dissipate
        }
      }

      if (state === 'fading') {
        if (sinkProgress > 0) {
          drawWaterEntry(homeX, H * 0.62, sinkProgress + (150 - stateTimer));
        }
        if (stateTimer <= 0) {
          state = 'waiting';
          stateTimer = 900 + Math.random() * 600; // 15-25s before next appearance
        }
      }

      // --- Blood / gold drips (always active, subtle) ---
      if (frame % 120 === 0 && state !== 'releasing') {
        drips.push(createDrip(false));
      }

      for (let i = drips.length - 1; i >= 0; i--) {
        const d = drips[i];
        d.progress += d.speed;
        if (d.progress > d.maxDrop) { drips.splice(i, 1); continue; }

        const dropY = d.y + d.progress;
        const fade = d.progress > d.maxDrop * 0.6 ? (d.maxDrop - d.progress) / (d.maxDrop * 0.4) : 1;
        const color = d.isGold ? `rgba(180, 150, 60, ${d.opacity * fade})` : `rgba(120, 20, 20, ${d.opacity * fade})`;

        // Drop
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(d.x, dropY, d.width * 0.4, 2 + d.progress * 0.05, 0, 0, Math.PI * 2);
        ctx.fill();

        // Trail
        ctx.strokeStyle = color.replace(/[\d.]+\)$/, `${d.opacity * fade * 0.3})`);
        ctx.lineWidth = d.width * 0.3;
        ctx.beginPath();
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x, dropY - 2);
        ctx.stroke();
      }

      animId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={720}
      height={400}
      className="absolute inset-0 w-full h-full pointer-events-none z-10"
    />
  );
};

const HeroSection = () => {
  const scrollToContent = () => {
    const element = document.getElementById('universe-intro');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed animate-water-ripple"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1766582931866-4bffcf038983?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxkYXJrJTIwcml2ZXIlMjBuaWdodCUyMHdhdGVyJTIwcmlwcGxlc3xlbnwwfHx8fDE3NzU1NTM0NDZ8MA&ixlib=rb-4.1.0&q=85')`,
            opacity: 0.35
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080C10]/40 via-[#080C10]/70 to-[#080C10]" />
        
        {/* Water ripple overlay effect */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-radial from-[#1A2535]/30 via-transparent to-transparent" />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="mb-6 relative"
        >
          <img
            src="/kow-site-logo.png"
            alt="KILLING OVER WATER"
            className="w-[340px] sm:w-[480px] md:w-[600px] lg:w-[720px] mx-auto drop-shadow-2xl relative z-0"
            data-testid="hero-logo"
          />
          <LogoAnimation />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="font-['Outfit'] text-[#E8E0D0]/70 text-base sm:text-lg tracking-widest uppercase mb-8"
          data-testid="hero-subtitle"
        >
          A Crime Fiction Universe
        </motion.p>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="w-32 h-px bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent mx-auto mb-8"
        />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="font-['Cormorant_Garamond'] text-[#E8E0D0] text-xl sm:text-2xl italic"
          data-testid="hero-tagline"
        >
          "The water always remembers."
        </motion.p>
      </div>

      {/* Scroll Indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        onClick={scrollToContent}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 text-[#C9A84C] animate-chevron cursor-pointer"
        data-testid="scroll-indicator"
        aria-label="Scroll to content"
      >
        <ChevronDown size={32} strokeWidth={1} />
      </motion.button>
    </section>
  );
};

export default HeroSection;
